import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import React, { useCallback } from "react";
import { useFragment } from "react-relay";

import {
  Box,
  ScrollView,
} from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import {
  currentSignUpRowAtom,
  eventSignUpProcessDispatchAtom,
} from "../atoms/eventSignUpProcess";
import { EventSignUpRegistration } from "../types";
import { EventDetailSignUpFormResponses } from "./EventDetailSignUpFormResponses";
import { FormStepHeader } from "./FormStepHeader";
import { EventFormStep_event$key } from "./__generated__/EventFormStep_event.graphql";

type EventFormStepProps = {
  eventRef: EventFormStep_event$key;
};
export const EventFormStep = ({ eventRef }: EventFormStepProps) => {
  const currentSignUpRow = useAtomValue(currentSignUpRowAtom);
  const dispatch = useSetAtom(eventSignUpProcessDispatchAtom);
  const event = useFragment(EventFormStepFragmentString, eventRef);
  const eventNoun = getNounOverride(
    event.eventSeries?.facility?.club?.copyOverrides?.event?.singular,
    t`Event`
  );
  const isGroupEvent =
    event?.maxGuestCount !== 0 ||
    event?.maxMemberCount !== 1 ||
    event?.minMemberCount !== 1 ||
    event?.minGuestCount !== 0;

  const transportationTypes =
    event.eventSeries?.facility?.transportationTypes?.edges?.map((t) => ({
      id: t?.node?.id ?? "",
      code: t?.node?.code ?? "",
      customCode: t?.node?.customCode ?? "",
    })) || [];

  const handleFormChange = useCallback(
    ({
      change,
      registration,
    }: {
      registration: EventSignUpRegistration;
      change: {
        id: string;
        value?: string;
        booleanValue?: boolean;
        transportationTypeId?: string;
      };
    }) => {
      const { value, booleanValue, transportationTypeId, id } = change;
      dispatch({
        type: "updateFormQuestionResponse",
        payload: {
          value,
          booleanValue,
          transportationTypeId,
          id,
          registration,
        },
      });
    },
    [dispatch]
  );

  // We are using this to filter out added member holds and unknown guests from the list
  // When added we apply a fake name to the registration so it can show correctly in the staff view
  const realMemberAndGuestRegistrations =
    currentSignUpRow?.registrations?.filter((r) => {
      const isMemberHold =
        r.type === "MEMBER" &&
        ((!r?.registrant?.firstName && !r?.registrant?.lastName) ||
          (r?.registrant?.firstName === "Member" &&
            r?.registrant?.lastName === "Hold"));
      const isUnknownGuest =
        r.type === "GUEST" &&
        ((!r?.registrant?.firstName && !r?.registrant?.lastName) ||
          (r?.registrant?.firstName === "Unknown" &&
            r?.registrant?.lastName === "Guest"));
      return !isMemberHold && !isUnknownGuest;
    });

  return (
    <Box flex={1}>
      <FormStepHeader title={t`${eventNoun} Form`} />

      <ScrollView pb={6} flex={1}>
        {realMemberAndGuestRegistrations?.map((registration) => {
          return (
            <Box
              key={`${registration.registrant?.lastName}${registration.registrant?.firstName}${registration.registrant?.playerId}`}
            >
              <EventDetailSignUpFormResponses
                registration={registration}
                transportationTypes={transportationTypes}
                handleFormChange={handleFormChange}
                isGroupEvent={isGroupEvent}
                copyOverrides={
                  event.eventSeries?.facility?.copyOverrides?.eventPlayer ??
                  undefined
                }
              />
            </Box>
          );
        })}
      </ScrollView>
    </Box>
  );
};

const EventFormStepFragmentString = graphql`
  fragment EventFormStep_event on Event {
    maxGuestCount
    maxMemberCount
    minGuestCount
    minMemberCount
    eventSeries {
      facility {
        club {
          copyOverrides {
            event {
              singular
            }
          }
        }
        copyOverrides {
          eventPlayer {
            singular
          }
        }
        transportationTypes(enabled: true) {
          edges {
            node {
              id
              code
              customCode
            }
          }
        }
      }
    }
  }
`;
